import type { ReactNode } from "react";
import { Trans } from "@lingui/react/macro";
import { cn } from "@/lib/utils";

/**
 * Shared building blocks for the account / profile tabs. Every tab follows the
 * same anatomy: a `TabHeader` (title + description + right-aligned actions),
 * then a stack of `FormSection`s — a two-column row whose left side labels the
 * group and whose right side is a `bg-(--color-surface-content)` card holding
 * the controls. `SectionDivider` separates sections; `Field`/`FieldGrid`/
 * `ReadonlyField` lay out individual controls; `SettingRow` is the icon + text +
 * action list row reused across Security / Billing / account actions.
 */

export function TabHeader({
  title,
  description,
  actions,
  tag,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  tag?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-semibold leading-7 text-(--color-text-primary)">
              {title}
            </h2>
            {tag}
          </div>
          {description ? (
            <p className="text-sm leading-5 text-(--color-text-secondary)">
              {description}
            </p>
          ) : null}
        </div>
        {actions ? (
          <div className="flex shrink-0 items-center gap-2">{actions}</div>
        ) : null}
      </div>
      <SectionDivider />
    </div>
  );
}

/** Small "work in progress" pill for tabs not yet fully implemented. */
export function WipTag() {
  return (
    <span className="inline-flex items-center rounded-full bg-(--color-warning-bg) px-2.5 py-0.5 text-xs font-semibold text-(--color-warning-text)">
      <Trans>WIP</Trans>
    </span>
  );
}

export function SectionDivider({ className }: { className?: string }) {
  return <div className={cn("h-px w-full bg-(--color-border)", className)} />;
}

/**
 * Two-column settings section. Left: the group label + supporting copy. Right:
 * a surface card with the controls (set `bare` to drop the card, e.g. for a
 * full-bleed list of `SettingRow`s).
 */
export function FormSection({
  title,
  description,
  children,
  bare = false,
  contentClassName,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  bare?: boolean;
  contentClassName?: string;
}) {
  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:gap-8">
      <div className="lg:w-100 lg:shrink-0">
        <p className="text-sm font-medium text-(--color-text-primary)">{title}</p>
        {description ? (
          <p className="mt-1 text-sm leading-5 text-(--color-text-secondary)">
            {description}
          </p>
        ) : null}
      </div>
      <div
        className={cn(
          "flex-1",
          !bare &&
            "rounded-xl border border-(--color-border) bg-(--color-surface-content) p-6",
          contentClassName,
        )}
      >
        {children}
      </div>
    </div>
  );
}

export function FieldGrid({ children }: { children: ReactNode }) {
  return <div className="grid gap-5 sm:grid-cols-2">{children}</div>;
}

export function Field({
  label,
  htmlFor,
  action,
  children,
  className,
}: {
  label: string;
  htmlFor?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <div className="flex items-center justify-between gap-2">
        <label
          htmlFor={htmlFor}
          className="text-xs font-medium text-(--color-text-primary)"
        >
          {label}
        </label>
        {action}
      </div>
      {children}
    </div>
  );
}

/** Read-only value rendered with the muted inset surface (e.g. verified email). */
export function ReadonlyField({
  value,
  adornment,
  className,
}: {
  value: ReactNode;
  adornment?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex h-11 items-center justify-between gap-2 rounded-lg border border-(--color-border) bg-(--color-surface-muted) px-3 text-sm text-(--color-text-primary)",
        className,
      )}
    >
      <span className="truncate">{value}</span>
      {adornment}
    </div>
  );
}

/** The small square icon tile that fronts setting rows / stat cards. */
export function IconTile({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex size-11 shrink-0 items-center justify-center rounded-lg bg-(--color-surface-muted) text-(--color-text-secondary)",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Icon + title/description + trailing action row. Used for sessions, payment
 *  methods, API tokens and the account-action lists. */
export function SettingRow({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-lg border border-(--color-border) p-4 sm:flex-row sm:items-center sm:justify-between",
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        {icon ? <IconTile>{icon}</IconTile> : null}
        <div className="min-w-0">
          <div className="text-sm font-medium text-(--color-text-primary)">
            {title}
          </div>
          {description ? (
            <div className="mt-0.5 text-sm text-(--color-text-secondary)">
              {description}
            </div>
          ) : null}
        </div>
      </div>
      {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
    </div>
  );
}
